import type { Gender } from '@common/enums';
import type { OpnameDuur } from '@common/enums/opnameDuur';
import type { TypeOnderzoek } from '@common/enums/typeOnderzoek';
import type { IGeneralPractitioner } from '@common/interfaces';
import type { Icd10Code } from '@common/interfaces/icd10';
import type { ServiceOrganizationProvider } from '@common/interfaces/serviceOrganizationProvider';
import type { CheckboxOption } from '@common/validation/customControls/nestedCheckboxGroupWithTextInput';
import type { ProcessActivityProfessionalSummaryData } from '@common/validation/customControls/processActivityProfessionalSummaryData';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import { YesNoNoDeviations } from '@common/enums/yesNoDeviations';
import type { CardiologyMedication } from './medicationUtils';

export enum RedenHolterEventOption {
    algemene_onderzoek = 'Algemene onderzoek',
    anders = 'Andere reden of toelichting',
    collapsneiging = 'Licht in het hoofd/collapsneiging',
    duizeligheid = 'Duizeligheid',
    dysponea = 'Dyspnoe',
    geruststelling = 'Geruststelling',
    hartkloppingen_overslagen = 'Hartkloppingen/overslagen',
    klachten = 'Klachten',
    moeheid = 'Moeheid',
    onregelmatige_pols = 'Onregelmatige pols',
    pijn_op_de_borst = 'Pijn op de borst',
    wegraking = 'Wegraking (bewustzijnsverlies)',
}

export const redenHolterEventOptions: Array<CheckboxOption> = [
    {
        key: 'algemene_onderzoek',
        label: RedenHolterEventOption.algemene_onderzoek,
    },
    {
        key: 'geruststelling',
        label: RedenHolterEventOption.geruststelling,
    },
    {
        children: [
            { key: 'hartkloppingen_overslagen', label: RedenHolterEventOption.hartkloppingen_overslagen },
            { key: 'onregelmatige_pols', label: RedenHolterEventOption.onregelmatige_pols },
            { key: 'collapsneiging', label: RedenHolterEventOption.collapsneiging },
            { key: 'duizeligheid', label: RedenHolterEventOption.duizeligheid },
            { key: 'wegraking', label: RedenHolterEventOption.wegraking },
            { key: 'dysponea', label: RedenHolterEventOption.dysponea },
            { key: 'moeheid', label: RedenHolterEventOption.moeheid },
            { key: 'pijn_op_de_borst', label: RedenHolterEventOption.pijn_op_de_borst },
        ],
        key: 'klachten',
        label: RedenHolterEventOption.klachten,
    },
    {
        isTextInput: true,
        key: 'anders',
        label: RedenHolterEventOption.anders,
    },
];

export enum RelevanteVoorgeschiedenisOption {
    anders = 'Andere voorgeschiedenis of toelichting...',
    boezemfibrilleren = 'Boezemfibrilleren/boezemflutter',
    copd = 'COPD',
    diabetes = 'Diabetes',
    extrasystoly = 'Extrasystolie (PAC’s/PVC’s)',
    geleidingstoornis = 'Geleidingstoornis',
    hartfalen = 'Hartfalen',
    hypertensie = 'Hypertensie',
    kleplijden = 'Kleplijden',
    myocardinfarct = 'Myocardinfarct (hartinfarct)',
    slaapapneu = 'Slaapapneu',
    super_verticulaire_tachycardie = 'Superventriculaire tachycardie, namelijk...',
    tia_cva = 'TIA/CVA',
    ventriculaire_tachycardie = 'Ventriculaire tachycardie, namelijk...',
}

export const relevanteVoorgeschiedenisOptions: RelevanteVoorgeschiedenisOption[] = [
    RelevanteVoorgeschiedenisOption.hypertensie,
    RelevanteVoorgeschiedenisOption.hartfalen,
    RelevanteVoorgeschiedenisOption.boezemfibrilleren,
    RelevanteVoorgeschiedenisOption.extrasystoly,
    RelevanteVoorgeschiedenisOption.ventriculaire_tachycardie,
    RelevanteVoorgeschiedenisOption.super_verticulaire_tachycardie,
    RelevanteVoorgeschiedenisOption.geleidingstoornis,
    RelevanteVoorgeschiedenisOption.tia_cva,
    RelevanteVoorgeschiedenisOption.copd,
    RelevanteVoorgeschiedenisOption.slaapapneu,
    RelevanteVoorgeschiedenisOption.diabetes,
    RelevanteVoorgeschiedenisOption.myocardinfarct,
    RelevanteVoorgeschiedenisOption.kleplijden,
    RelevanteVoorgeschiedenisOption.anders,
];

export const relevanteVoorgeschiedenisOptionsWithTextInput: string[] = [
    'ventriculaire_tachycardie',
    'super_verticulaire_tachycardie',
    'anders',
];

export enum FamilyRiskFactorOption {
    cardiomyopathy = 'Cardiomyopathie',
    heart_valve_disease = 'Klepafwijking',
    myocardinfarct = 'Myocardinfarct',
    pacemaker_implementation = 'Pacemaker implementatie',
    pci_cabg = 'PCI of CABG < 60 jaar, bij 1e-graads familieleden',
    sudden_cardiac_death = 'Plotselinge hartdood',
    unknown = 'Onbekend',
}

export const familyRiskFactorOptions: FamilyRiskFactorOption[] = [
    FamilyRiskFactorOption.myocardinfarct,
    FamilyRiskFactorOption.pci_cabg,
    FamilyRiskFactorOption.cardiomyopathy,
    FamilyRiskFactorOption.pacemaker_implementation,
    FamilyRiskFactorOption.sudden_cardiac_death,
    FamilyRiskFactorOption.heart_valve_disease,
    FamilyRiskFactorOption.unknown,
];

export enum FemaleRiskFactorOption {
    hypertension_before_menopause = 'Hypertensie voor menopauze',
    hypertension_during_pregnancy = 'Hypertensie of diabetis mellitus tijdens zwangerschap',
    menopause = 'Start overgang < 50 jaar',
    miscarriage = 'Miskraam',
}

export const femaleRiskFactorOptions: FemaleRiskFactorOption[] = [
    FemaleRiskFactorOption.miscarriage,
    FemaleRiskFactorOption.hypertension_during_pregnancy,
    FemaleRiskFactorOption.hypertension_before_menopause,
    FemaleRiskFactorOption.menopause,
];

export enum GeneralRiskFactorOption {
    adiposity = 'Adipositas',
    alcohol = 'Alcohol',
    cardiovascular_disease = 'Positieve familieanamnese voor hart- en vaatziekten',
    diabetes_mellitus = 'Diabetes mellitus',
    drugs = 'Drugs',
    dyslipidemia = 'Dyslipidemie',
    hypertension = 'Hypertensie',
    intoxications = 'Intoxicaties',
    migraines = 'Migraine',
    rheumatic_disease = 'Reumatische aandoening',
    smoking = 'Roken',
    thyroid_dysfunction = 'Thyroïd (schildklier) disfunctie',
}

export const generalRiskFactorOptions: Array<CheckboxOption> = [
    {
        key: 'hypertension',
        label: GeneralRiskFactorOption.hypertension,
    },
    {
        key: 'migraines',
        label: GeneralRiskFactorOption.migraines,
    },
    {
        key: 'dyslipidemia',
        label: GeneralRiskFactorOption.dyslipidemia,
    },
    {
        key: 'cardiovascular_disease',
        label: GeneralRiskFactorOption.cardiovascular_disease,
    },
    {
        key: 'diabetes_mellitus',
        label: GeneralRiskFactorOption.diabetes_mellitus,
    },
    {
        key: 'adiposity',
        label: GeneralRiskFactorOption.adiposity,
    },
    {
        key: 'rheumatic_disease',
        label: GeneralRiskFactorOption.rheumatic_disease,
    },
    {
        key: 'thyroid_dysfunction',
        label: GeneralRiskFactorOption.thyroid_dysfunction,
    },

    {
        children: [
            { key: 'alcohol', label: GeneralRiskFactorOption.alcohol },
            { key: 'drugs', label: GeneralRiskFactorOption.drugs },
            { key: 'smoking', label: GeneralRiskFactorOption.smoking },
        ],
        key: 'intoxications',
        label: GeneralRiskFactorOption.intoxications,
    },
];

export const yesNoNoDeviationsOptions: YesNoNoDeviations[] = [
    YesNoNoDeviations.yes,
    YesNoNoDeviations.no,
    YesNoNoDeviations.no_deviations,
];

export type RedenHolterEvent = {
    algemene_onderzoek: boolean;
    anders: string | null;
    collapsneiging: boolean;
    duizeligheid: boolean;
    dysponea: boolean;
    geruststelling: boolean;
    hartkloppingen_overslagen: boolean;
    klachten: boolean;
    moeheid: boolean;
    onregelmatige_pols: boolean;
    pijn_op_de_borst: boolean;
    wegraking: boolean;
};

export type RelevanteVoorgeschiedenis = {
    anders: string | null;
    boezemfibrilleren: boolean;
    copd: boolean;
    diabetes: boolean;
    extrasystoly: boolean;
    geleidingstoornis: boolean;
    hartfalen: boolean;
    hypertensie: boolean;
    kleplijden: boolean;
    myocardinfarct: boolean;
    slaapapneu: boolean;
    super_verticulaire_tachycardie: string | null;
    tia_cva: boolean;
    ventriculaire_tachycardie: string | null;
};

export type HolterReferralData = {
    generalPractitioner: IGeneralPractitioner;
};

export type HolterReferralRecipientData = {
    recipient: ServiceOrganizationProvider;
};

export type HolterReferralAssetsData = {
    assets: ProcessActivityAsset[];
};

export type HolterEcgData = {
    ecg: boolean | null;
    ecg_afwijkingen: boolean | null;
    ecg_afwijkingen_opmerkingen: string | null;
};

export type HolterAnamnesisData = {
    hasPacemaker: boolean | null;
    reden_holter_event: RedenHolterEvent;
};

export type MedicationData = {
    medication: CardiologyMedication;
    patientUsesMedication: boolean | null;
};

export type HolterHistoryData = {
    relevante_voorgeschiedenis: RelevanteVoorgeschiedenis;
};

export type HolterExaminationRequestBase = {
    opnameduur: OpnameDuur | null;
    patient_geslacht: Gender | null;
    type_onderzoek: TypeOnderzoek | null;
} & ProcessActivityProfessionalSummaryData &
    HolterHistoryData &
    HolterEcgData &
    HolterAnamnesisData &
    MedicationData;

export type DiagnosisData = {
    diagnosis_icd10: Icd10Code | null;
    hasDiagnosis: YesNoNoDeviations | null;
};

export type HolterExaminationRequestAdvice = {
    meedenkadvies_cardioloog: boolean | null;
    vraag_aan_specialist: string | null;
} & HolterRiskFactorsData &
    DiagnosisData;

export type HolterRiskFactorsData = {
    familial_riskfactors: {
        cardiomyopathy: boolean;
        heart_valve_disease: boolean;
        myocardinfarct: boolean;
        pacemaker_implementation: boolean;
        pci_cabg: boolean;
        sudden_cardiac_death: boolean;
        unknown: boolean;
    };
    female_riskfactors: {
        hypertension_before_menopause: boolean;
        hypertension_during_pregnancy: boolean;
        menopause: boolean;
        miscarriage: boolean;
    };
    general_riskfactors: {
        adiposity: boolean;
        alcohol: boolean;
        cardiovascular_disease: boolean;
        diabetes_mellitus: boolean;
        drugs: boolean;
        dyslipidemia: boolean;
        hypertension: boolean;
        migraines: boolean;
        rheumatic_disease: boolean;
        smoking: boolean;
        thyroid_dysfunction: boolean;
    };
    riskfactors_remark: string | null;
};
export { YesNoNoDeviations };
